import type { ReactNode } from 'react';
import { useMemo } from 'react';

import {
  DropdownButton,
  DropdownButtonProps,
} from '@/ux-common/components/dropdown-button/dropdown-button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/ux-common/components/dropdown-menu/dropdown-menu';
import { Inline } from '@/ux-common/components/inline/inline';
import { withTooltip } from '@/ux-common/components/tooltip/with-tooltip';
import { cn } from '@/ux-common/lib/utils';

const DropdownButtonWithTooltip = withTooltip(DropdownButton);

export interface DropdownCellOption {
  value: string;
  label: string;
  /**
   * Optional icon node rendered before the label on this option's menu item, and
   * on the trigger while the option is selected unless `showTriggerIcon` is off.
   */
  icon?: ReactNode;
}

/** `DropdownButtonProps` without the keys this component owns. */
type BaseButtonProps = Omit<DropdownButtonProps, 'onSelect' | 'value'>;

export interface DropdownCellProps<T extends DropdownCellOption = DropdownCellOption>
  extends BaseButtonProps {
  /** Currently selected value (controlled). */
  value: string | null | undefined;
  /** Selectable options. */
  options: T[];
  /** Called with the full option when the user selects one. */
  onSelect: (option: T) => void;
  /**
   * When provided and a value is selected, renders a "Clear selection" item at
   * the top of the menu that invokes this handler.
   */
  onClear?: () => void;
  /**
   * Disable the trigger so the menu can't be opened. Use when the control is
   * unavailable outright (e.g. a precondition isn't met); surface the reason
   * via `tooltipContent`, shown on hover of the disabled trigger.
   */
  disabled?: boolean;
  /**
   * Grey out the menu items while still allowing the menu to open. Use when the
   * user may view the available options but isn't permitted to choose one (e.g.
   * permission-gated rows). The trigger stays interactive; pair with
   * `tooltipContent`, which becomes each row's refusal reason.
   */
  disableItems?: boolean;
  /** Heading rendered above the options. */
  subtitle?: string;
  /** Placeholder shown when nothing is selected. */
  placeholder?: string;
  /**
   * Whether the selected option's icon also shows on the trigger. Menu items
   * always render their own icon; only the trigger is optional, so a cell can
   * stay text-only while the menu still identifies each choice by its glyph.
   */
  showTriggerIcon?: boolean;
  /** `data-state-name` applied to the trigger. */
  stateName?: string;
  /** Tooltip shown on trigger hover — typically explains a `disabled`/`disableItems` state. */
  tooltipContent?: string;
  /** Classes applied to the trigger button (e.g. variant, full-bleed styling). */
  className?: string;
  /** Classes applied to the menu content (e.g. width, max-height, z-index). */
  contentClassName?: string;
  /** Message shown when there are no selectable options. */
  emptyMessage?: string;
}

/**
 * Dropdown cell renderer.
 *
 * Controlled, presentation-only dropdown built on the Radix `DropdownMenu` +
 * `DropdownButton` primitives. Renders the selected option's label (with an
 * optional injected icon and tooltip) on the trigger and a menu of the
 * remaining options — the currently selected option is hidden from the list.
 * Supports an optional clear action and an empty state.
 *
 * Styling (button variant, full-bleed cell appearance, menu width/z-index) is
 * supplied entirely through `className`/`contentClassName`, so the component
 * stays decoupled from any feature.
 */
export const DropdownCell = <T extends DropdownCellOption = DropdownCellOption>({
  disabled,
  disableItems,
  options,
  subtitle,
  onSelect,
  onClear,
  placeholder,
  showTriggerIcon = true,
  stateName,
  tooltipContent,
  value,
  className,
  contentClassName,
  emptyMessage = 'No options available',
  ...props
}: DropdownCellProps<T>) => {
  const currentOption = useMemo(
    () => options.find((option) => option.value === value),
    [options, value]
  );

  const visibleOptions = useMemo(
    () => options.filter((option) => option.value !== value),
    [options, value]
  );

  const showClear = onClear !== undefined && value !== null && value !== undefined;

  // A permission-gated row is refused rather than natively disabled, so it keeps
  // its place in the Tab order and carries the reason it cannot be chosen. Both
  // flags are passed as-is: the vendored item owns the precedence — a reason wins
  // over `disabled`, and without one the native flag is the fallback that keeps a
  // reasonless `disableItems` from leaving the rows selectable.
  const itemDisabledReason = disableItems ? tooltipContent : undefined;
  const itemDisabled = disabled || disableItems;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-state-name={stateName}
        asChild
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <div
          className="h-full w-full"
          data-component="dropdown-cell"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <DropdownButtonWithTooltip
            className={cn(
              'btn-sm w-full [&>span:first-child]:min-w-0 [&>span:first-child]:overflow-hidden',
              className
            )}
            tooltipArrow
            tooltipContent={tooltipContent}
            {...props}
          >
            <Inline className="flex flex-row flex-nowrap gap-x-2 min-w-0 overflow-hidden">
              {showTriggerIcon && currentOption?.icon}
              <span className="truncate">
                {currentOption?.label ?? value?.toString() ?? placeholder ?? 'Unknown'}
              </span>
            </Inline>
          </DropdownButtonWithTooltip>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className={cn('min-w-72', contentClassName)}
        data-component="dropdown-cell-content"
        collisionPadding={8}
        onClick={(e) => e.stopPropagation()}
      >
        {subtitle && (
          <>
            <DropdownMenuLabel>{subtitle}</DropdownMenuLabel>
            <DropdownMenuSeparator />
          </>
        )}
        {showClear && (
          <>
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                onClear?.();
              }}
            >
              Clear selection
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        {visibleOptions.length === 0 ? (
          <DropdownMenuItem disabled>{emptyMessage}</DropdownMenuItem>
        ) : (
          visibleOptions.map((option) => (
            <DropdownMenuItem
              data-component="dropdown-cell-option"
              data-value={option.value}
              disabled={itemDisabled}
              disabledReason={itemDisabledReason}
              key={option.value}
              onClick={(e) => {
                e.stopPropagation();
                // Guarded here, not by looks: a natively disabled Radix item is a
                // <div> whose data-disabled only styles it — the registry CSS has no
                // pointer-events rule, and Radix guards its own onSelect, never a
                // consumer onClick. Without this, a gated row still selects on click.
                // (The refused path needs no guard — the vendored item swaps the
                // handler for preventDefault — but it costs nothing there.)
                if (itemDisabled) return;
                onSelect(option);
              }}
            >
              {option.icon}
              {/* The label is a bare child, as the registry row expects: the row
                  owns its text in every state — pressed turns it brand green on
                  black, refused greys it out — and a colour set on a wrapper here
                  would win over all of them, since a declaration on the element
                  beats what it would inherit. */}
              {option.label}
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

DropdownCell.displayName = 'DataTable.DropdownCell';
